import { map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { SmsConfigRequest, SmsConfigResponse, SmsRateLimitsResponse } from 'voxloud-types/dist/models';

import { environment } from 'src/environments/environment';
import { AdditionalCompanyData, Company, CONTROL_PANEL_ENVIRONMENT, KazooMetadata, NotesModel, UpdateCustomerCompanyRequest, ContactsConfiguration, CompanyChatInfo } from '../models/company.model';

@Injectable()
export class CompanyService {
  constructor(private http: HttpClient) { }

  getCustomerCompany(id: number): Observable<AdditionalCompanyData> {
    return this.http.get<AdditionalCompanyData>(`${environment.apiEndpoint}/api/v1/customer-support-companies/${id}`);
  }

  getCompany(id: number): Observable<Company> {
    return this.http.get<Company>(`${environment.apiEndpoint}/api/v2/companies/${id}`)
  }

  updateCompany(company: Company): Observable<Company> {
    return this.http.put<Company>(
      `${environment.apiEndpoint}/api/v2/companies/${company.companyId}`,
      company
    );
  }

  updateCustomerSupportCompany(data: UpdateCustomerCompanyRequest ): Observable<AdditionalCompanyData> {
    return this.http.put<AdditionalCompanyData>(
      `${environment.apiEndpoint}/api/v1/customer-support-companies`,
      data
    );
  }

  deleteCompany(company: Company): Observable<void> {
    return this.http.delete<void>(`${environment.apiEndpoint}/api/v2/companies/${company.companyId}`)
  }

  toggleReadOnlyMode(company: Company) {
    return this.http.post<void>(
      `${environment.apiEndpoint}/api/v2/companies/${company.companyId}/read-only`,
      { readOnly: company.readOnly }
    )
  }

  unlockCompany(company: Company): Observable<void> {
    return this.http.put<void>(
      `${environment.apiEndpoint}/api/unlockaccount/${company.companyId}`,
      null
    );
  }

  lockCompany(company: Company): Observable<void> {
    return this.http.put<void>(
      `${environment.apiEndpoint}/api/lockaccount/${company.companyId}`,
      null
    );
  }

  getCredit(id: number): Observable<any> {
    return this.http.get<any>(`${environment.apiEndpoint}/api/v2/companies/${id}/credit`);
  }

  addCredit(id: number, value: number): Observable<any> {
    return this.http.post(
      `${environment.apiEndpoint}/api/v2/companies/${id}/credit/add`,
      { value }
    );
  }

  toggleRecordings(id: number): Observable<any> {
    return this.http.post<Observable<any>>(
      `${environment.apiEndpoint}/api/v2/companies/${id}/toggle-recordings`,
      null
    );
  }

  updateUserPassword(id: number, user: number, password: string) {
    return this.http.post(
      `${environment.apiEndpoint}/api/v2/companies/${id}/users/${user}/setpassword`,
      { password }
    );
  }

  updateCompanyNotes(id: number, notes: NotesModel): Observable<AdditionalCompanyData> {
    return this.http.put<AdditionalCompanyData>(
      `${environment.apiEndpoint}/api/v1/customer-support-companies/${id}/notes`,
      notes
    );
  }


  openControlPanel(
    company: Company,
    env: CONTROL_PANEL_ENVIRONMENT
  ): void {
    const controlPanelUrl = env === CONTROL_PANEL_ENVIRONMENT.PRODUCTION
      ? environment.controlpanelUrl
      : environment.stagingControlpanelUrl;
    let encodedPwd: string = this.getPassword(company.password);
    window
      .open(
        `${controlPanelUrl}/login/#/signin?admin=true&pw=${encodeURIComponent(encodedPwd)}&us=${encodeURIComponent(company.email)}`,
        '_blank'
      )
      .focus();
  }

  getWIPLink(company: Company, wip: string) {
    const controlPanelUrl = `https:\/\/${wip}-controlpanel.voxloud.com`;
    let encodedPwd: string = this.getPassword(company.password);
    return `${controlPanelUrl}/login/#/signin?admin=true&pw=${encodeURIComponent(encodedPwd)}&us=${encodeURIComponent(company.email)}`;
  }

  openWIPLink(company: Company, wip: string) {
    window
      .open(
        this.getWIPLink(company, wip),
        '_blank'
      )
      .focus();
  }

  private getPassword(password: string): string {
    return password.includes('%') 
    ? password.replace(/%/g, '%25')
    : password
  }

  unlockKazooAccount(pbxId: number){
    return this.http.put(`${environment.apiEndpoint}/api/v1/pbxes/${pbxId}/kazoo-account/enable`, {});
  }

  lockKazooAccount(pbxId: number){
    return this.http.put(`${environment.apiEndpoint}/api/v1/pbxes/${pbxId}/kazoo-account/disable`, {});
  }

  toggelContactsConfiguration(companyId: number, data) {
    return this.http.put(`${environment.apiEndpoint}/api/v1/contacts/configuration/${companyId}`, data);
  }

  getContactsConfiguration(companyId: number) {
    return this.http.get<ContactsConfiguration>(`${environment.apiEndpoint}/api/v1/contacts/configuration/${companyId}`);
  }

  toggleSmsConfiguration(locked: boolean, companyId: number) {
    return this.http.put(`${environment.apiEndpoint}/api/v1/sms/configs/lock-state?locked=${locked}`, {}, {
      headers: {
        'Voverc-Company-ID': String(companyId)
      }
    });
  }

  getSmsConfiguration(companyId: number): Observable<SmsConfigResponse> {
    return this.http.get<SmsConfigResponse>(`${environment.apiEndpoint}/api/v1/sms/configs`, {
      headers: {
        'Voverc-Company-ID': String(companyId)
      }
    });
  }

  updateSmsConfiguration(companyId: number, smsConfigRequest: SmsConfigRequest): Observable<SmsConfigResponse> {
    return this.http.put<SmsConfigResponse>(`${environment.apiEndpoint}/api/v1/sms/configs`, smsConfigRequest, {
      headers: {
        'Voverc-Company-ID': String(companyId)
      }
    });
  }
  getSmsRateLimits(companyId: number): Observable<SmsRateLimitsResponse> {
    return this.http.get<SmsRateLimitsResponse>(`${environment.apiEndpoint}/api/v1/sms/configs/rate-limits`, {
      headers: {
        'Voverc-Company-ID': String(companyId)
      }
    });
  }

  getCompanyChatInfo(companyId: number): Observable<CompanyChatInfo> {
    return this.http.get<CompanyChatInfo>(`${environment.apiEndpoint}/api/v1/chat/customers/${companyId}`, {
      headers: {
        'Voverc-Company-ID': String(companyId)
      }
    })
  }
}
